import {
  Grid,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useContext } from "react";
import { formatSsn, getAgeInYearsFromSsnAtDate, getScreeningStatusText } from "../../utils";

import { CancerScreeningResultModel } from "../../models/cancerScreeningResultModel";
import { DATE_FORMAT } from '../../constants';
import { ICancerScreeningRequestDetails } from "../../interfaces/cancerScreeningRequest";
import { LangContext } from "../../context/LangContext";
import moment from "moment";
import translations from "../../assets/json/translations.json";

interface IProps {
  screeningRequest: CancerScreeningResultModel;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
  descriptionTitle: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
  },
  descriptionDetails: {
    color: theme.palette.text.secondary,
  },
}));

const ScreeningRequestDetail: React.FC<IProps> = ({
  screeningRequest
}) => {
  const classes = useStyles({});
  const theme = useTheme();
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const [lang] = useContext(LangContext);

  const screeningRequestDetails: ICancerScreeningRequestDetails[] = 
  [
    {
      label: translations.name[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.personName,
    },
    {
      label: translations.SSN[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        formatSsn(screeningRequest.personSsn),
    },
    {
      label: translations.age[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
      getAgeInYearsFromSsnAtDate(screeningRequest.personSsn, screeningRequest.examinationDateTime, lang),
    },
    {
      label: translations.examinationDate[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.examinationDateTime
          ? moment(screeningRequest.examinationDateTime).format(DATE_FORMAT)
          : translations.notRegistered[lang],
    },
    {
      label: translations.category[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.examinationReasonDescription
          ? screeningRequest.examinationReasonDescription.toString()
          : undefined,
    },
    {
      label: translations.specimenId[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.specimenId
          ? screeningRequest.specimenId.toString()
          : undefined,
    },
    {
      label: translations.status[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.screeningStatus !== undefined
          ? getScreeningStatusText(screeningRequest.screeningStatus, lang)
          : undefined,
    },
    {
      label: translations.registeredBy[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.requestorName,
    },
    {
      label: translations.registrationDate[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.createDateTime
          ? moment(screeningRequest.createDateTime).format(DATE_FORMAT)
          : translations.notRegistered[lang],
    },
    {
      label: translations.companyOperator[lang],
      render: (screeningRequest: CancerScreeningResultModel) => (
        <>
          <Typography>
            {screeningRequest.locationNumber}
          </Typography>
          <Typography>
            {screeningRequest.companyOperatorName}
          </Typography>
          <Typography >
            {screeningRequest.companyOperatorLocation}
          </Typography>
        </>),
    },
    {
      label: translations.comment[lang],
      getVal: (screeningRequest: CancerScreeningResultModel) =>
        screeningRequest.examinationNotes,
    },
  ];

  return (
    <Grid container spacing={xsDown ? 1 : 3} direction="row">
      <Grid item xs={12} sm={4}>
        <Grid container spacing={1}>
          {screeningRequestDetails.slice(0, 3).map((item, index) => (
            <Grid item xs={12} key={index}>
              {renderInfoItem(item)}
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Grid container spacing={1}>
          {screeningRequestDetails.slice(3, 7).map((item, index) => (
            <Grid item xs={12} key={index}>
              {renderInfoItem(item)}
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Grid container spacing={1}>
          {screeningRequestDetails.slice(7, 10).map((item, index) => (
            <Grid item xs={12} key={index}>
              {renderInfoItem(item)}
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12}>
        <Grid container spacing={1}>
          {screeningRequestDetails.slice(10).map((item, index) => (
            <Grid item xs={12} key={index}>
              {renderInfoItem(item)}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  function renderInfoItem({
    label,
    getVal = () => "",
    render,
    hide,
  }: ICancerScreeningRequestDetails) {
    if (hide && hide(screeningRequest)) {
      return null;
    }
    return (
      <Typography style={{ position: "relative", display: "flex" }}>
        <span className={classes.descriptionTitle}>{label}:</span>
        <span className={classes.descriptionDetails}>
          {getVal && getVal(screeningRequest)}
          {render && render(screeningRequest)}
        </span>
      </Typography>
    );
  }
};
export default ScreeningRequestDetail;
